import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', {
        email: email.trim(),
        password
      });
      login(data.token);
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Falha no login:', err);
      const msg =
        err.response?.data?.error ||
        (err.code === 'ERR_NETWORK'
          ? 'Não foi possível conectar ao servidor backend (porta 3001). Verifique se o backend está rodando.'
          : 'Email ou senha inválidos. Verifique suas credenciais.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-wrap">
      <div className="login-visual">
        <div className="brand">
          <div className="brand-mark">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="10" r="3" />
              <path d="M12 2a8 8 0 0 0-8 8c0 5.4 8 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z" />
            </svg>
          </div>
          <div className="brand-name">
            Otimiza<span>Rota</span>
          </div>
        </div>

        <div className="login-hero">
          <div className="login-eyebrow">
            <span className="dot" />
            Roteirização inteligente para transportadoras
          </div>
          <h1 className="login-h1">
            Menos combustível.<br />
            Mais entregas no prazo.
          </h1>
          <p className="login-lede">
            Planeje, sequencie e acompanhe rotas urbanas e intermunicipais com precisão milimétrica, controle de janelas de horário e economia comprovada.
          </p>

          <div className="login-metrics">
            <div className="metric-tile">
              <div className="n">2h 40min</div>
              <div className="l">Economia média / dia</div>
            </div>
            <div className="metric-tile">
              <div className="n">17,3%</div>
              <div className="l">Redução em combustível</div>
            </div>
            <div className="metric-tile">
              <div className="n">1.240+</div>
              <div className="l">Frotas ativas</div>
            </div>
          </div>
        </div>
      </div>

      <div className="login-form-wrap">
        <div className="login-card">
          <h2>Acesse sua conta</h2>
          <p className="sub">Digite suas credenciais para gerenciar sua frota</p>

          {error && <p className="msg-error">{error}</p>}

          <form onSubmit={handleSubmit}>
            <div className="field">
              <label htmlFor="email">Email corporativo</label>
              <input
                id="email"
                className="input"
                type="email"
                placeholder="seu.email@empresa.com.br"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="password">Senha de acesso</label>
              <div className="input-wrap">
                <input
                  id="password"
                  className="input"
                  type={showPw ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowPw((s) => !s)}
                  tabIndex={-1}
                  aria-label="Alternar exibição da senha"
                >
                  {showPw ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-2)', cursor: 'pointer', textTransform: 'none', margin: 0 }}>
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Lembrar meu acesso
              </label>
              <a href="#forgot" onClick={(e) => { e.preventDefault(); alert('Instruções enviadas para seu e-mail cadastrado.'); }} style={{ fontSize: 13, color: 'var(--teal-800)', fontWeight: 500 }}>
                Esqueceu a senha?
              </a>
            </div>

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
              {loading ? 'Entrando na plataforma...' : 'Entrar na plataforma'}
            </button>
          </form>

          <p className="login-foot">
            Não tem uma conta corporativa? <Link to="/register">Falar com consultor</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
